import { ChangeDetectionStrategy, Component, computed, inject, Signal, signal, WritableSignal } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AuthService } from '@app/core/shared/auth.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { User } from '@shared/graphql/generated';
import {
  ResetPasswordModalStoreService
} from '@shared/modals/reset-password-modal/shared/reset-password-modal-store.service';
import { ToastType } from '@shared/models/toast-type';
import { ToastStoreService } from '@shared/stores/toast-store.service';
import { UserStoreService } from '@shared/stores/user-store.service';
import { FormFieldComponent } from '@shared/ui-components/form-field/form-field.component';
import { InputDirective } from '@shared/ui-components/form-field/shared/input.directive';
import { ModalContainerComponent } from '@shared/ui-components/modal-container/modal-container.component';
import { SubmitButtonComponent } from '@shared/ui-components/submit-button/submit-button.component';
import { EvlandValidators } from '@shared/utils/validators';
import { finalize, of, switchMap } from 'rxjs';

@Component({
  selector: 'evland-reset-password-modal',
  standalone: true,
  imports: [
    ModalContainerComponent,
    ReactiveFormsModule,
    SubmitButtonComponent,
    FormFieldComponent,
    InputDirective
  ],
  templateUrl: './reset-password-modal.component.html',
  styleUrl: './reset-password-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResetPasswordModalComponent {
  private activeModal = inject(NgbActiveModal);
  private authService = inject(AuthService);
  private fb = inject(UntypedFormBuilder);
  private toast = inject(ToastStoreService);
  private resetPasswordStore = inject(ResetPasswordModalStoreService);
  private userStore = inject(UserStoreService);

  currentUser: Signal<User>;
  modalTitle: Signal<string>;
  form: UntypedFormGroup;
  passwordHint = $localize`Пароль має містити як мінімум 1 велику та 1 нижню літери, 1 цифру, 1 символ (@$!%*?&), та довжину від 8 до 24 символів`;
  isSubmitting: WritableSignal<boolean> = signal(false);

  constructor() {
    this.initForm();
    this.initSignals();
  }

  private initSignals(): void {
    this.currentUser = this.userStore.currentS;

    this.modalTitle = computed(() => !!this.currentUser() && !this.currentUser().has_password ? $localize `Встановлення пароля` : $localize `Скидання пароля`);
  }

  onClose(): void {
    this.activeModal.close();
  }

  onSubmit(): void {
    if (this.form.invalid || this.form.pristine || this.isSubmitting()) return;

    this.isSubmitting.set(true);

    const token = this.resetPasswordStore.currentS();

    const input = {
      ...(token && { token }),
      ...this.form.value,
    };

    this.authService.resetPassword(input)
      .pipe(
        switchMap(() => this.currentUser() ? this.authService.me() : of(null)),
        finalize(() => this.isSubmitting.set(false)),
      )
      .subscribe({
        next: () => {
          this.toast.show({
            text: $localize`Новий пароль успішно збережено`,
            type: ToastType.Success,
          });

          this.activeModal.close();
        },
        error: () => this.toast.showCommonError(),
      });
  }

  private initForm(): void {
    this.form = this.fb.group({
      password: [ '', [ Validators.required, EvlandValidators.passwordComplexity ] ],
      confirm_password: [ '', Validators.required ],
    }, { validators: [ EvlandValidators.matchPassword('password', 'confirm_password') ] });
  }
}
